/* eslint-disable @typescript-eslint/dot-notation */
import { Injectable } from '@angular/core';
import { DateService } from 'src/app/util/common/date-service/date.service';
import { ExcelGeneralLedgerDTO } from '../../models/DTOs/gl-upload/ExcelGeneralLedgerDTO';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { SpinnerService } from 'src/app/core/service/spinner.service';
import * as XLSX from 'xlsx-js-style';
import { ColumnSelector } from '../../utils/gl/utils';
import { cloneDeep } from 'lodash';

@Injectable()
export class GlUploadService {
  private _columnSelectors = new BehaviorSubject<ColumnSelector[]>(baseColumnSelectors);
  private _dataForDisplay = new BehaviorSubject<Record<string, string | number>[]>([]);
  private reservedDataForDisplay: Record<string, string | number>[];

  private worker: Worker;

  reportingPeriod: string;
  file;
  excelData: Record<string, string | number>[];
  headers: string[]; // excel headers
  glData: ExcelGeneralLedgerDTO[];
  selectedHeaders: string[];
  postPeriodData: ExcelGeneralLedgerDTO[] = [];
  allMandatoryColumnsSelected = false;

  constructor(
    private notificationsService: NotificationsService,
    private spinnerService: SpinnerService,
  ) {
    this.worker = new Worker('./excelWorker.worker', { type: 'module' });
    this.worker.addEventListener('message', this.onWorkerMessage.bind(this));
  }

  get columnSelectors(): BehaviorSubject<ColumnSelector[]> {
    return this._columnSelectors;
  }

  get dataForDisplay(): BehaviorSubject<Record<string, string | number>[]> {
    return this._dataForDisplay;
  }

  startSpinner(): void {
    this.spinnerService.addActiveRequest();
    this.spinnerService.removeActiveRequest();
  }

  // convert data objects from excel into financialDataObjects
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  convertExcelDataToFinancialStatementData(newFinStatData: Record<string, any>[],
    currentColumnSelectors: ColumnSelector[]): ExcelGeneralLedgerDTO[] {
    newFinStatData = this.convertAllDates();
    const dataForFinancialStatement: ExcelGeneralLedgerDTO[] = [];
    newFinStatData.forEach((dataObj) => {
      const financialStatementData = new ExcelGeneralLedgerDTO();
      financialStatementData.accountCode = dataObj[currentColumnSelectors.find((column) => column.title === 'Account #').header];
      financialStatementData.accountName = dataObj[currentColumnSelectors.find((column) => column.title === 'Account name').header];
      financialStatementData.transactionDate = DateService
        .createUTCDate(dataObj[currentColumnSelectors.find((column) => column.title === 'Date').header]);
      financialStatementData.journalEntry = dataObj[currentColumnSelectors.find((column) => column.title === 'Journal entry #').header];
      financialStatementData.description = dataObj[currentColumnSelectors.find((column) => column.title === 'Entry description').header];
      financialStatementData.debit = dataObj[currentColumnSelectors.find((column) => column.title === 'Debit').header];
      financialStatementData.credit = dataObj[currentColumnSelectors.find((column) => column.title === 'Credit')?.header] || 0;
      financialStatementData.contraAccountCode = dataObj[currentColumnSelectors.find((column) =>
        column.title === 'Contra account #').header];
      financialStatementData.isManualEntry = false;
      dataForFinancialStatement.push(financialStatementData);
    });
    this.glData = dataForFinancialStatement;
    return this.glData;
  }

  isColumnDateConverted(dataForDisplay: Record<string, string | number>[], dateColumn: string): boolean {
    const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    for (const rowData of dataForDisplay) {
      const dateValue = String(rowData[dateColumn]);
      if (!dateFormatRegex.test(dateValue)) {
        return false;
      }
    }
    return true;
  }


  /**
   * Converts a date value into a formatted string representation.
   *
   * This method takes a date value and converts it into a formatted string representation in the format "MM/DD/YYYY".
   * It supports two different types of date inputs:
   * - If the date value represents a valid date after January 1, 1970,
   *   it is treated as a regular ts date and directly converted to the desired format.
   * - If the date value is a numeric value representing the number of days since January 1, 1900 (Excel date format),
   *   it is converted to a JavaScript date before formatting.
   *
   * @param date - The date value to be converted. It can be a valid date,
   *   or a numeric value representing the number of days since January 1, 1900 (Excel date format).
   * @returns The formatted date string in the format "MM/DD/YYYY".
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  convertExcelDate(date: any): string {
    let fullDate: Date;

    // check if the provided date is a valid ts date (after 1970)
    if (new Date(date).getFullYear() > 1970) {
      fullDate = new Date(date);
    } else {
      // convert the Excel date value to a ts date by applying the necessary calculations
      fullDate = new Date(Math.round((date - 25569) * 86400 * 1000));
    }

    // format the date into "MM/DD/YYYY" format
    const finalDate = ((fullDate.getMonth() > 8) ? (fullDate.getMonth() + 1) : ('0' + (fullDate.getMonth() + 1)))
      + '/' + ((fullDate.getDate() > 9) ? fullDate.getDate() : ('0' + fullDate.getDate())) + '/' + fullDate.getFullYear();

    return finalDate;
  }

  checkIsDateValueCorrect(date: unknown): boolean {
    if (typeof date !== 'string') {
      return false;
    }
    if (date.includes('NaN')) {
      return false;
    }
    const year = parseInt(date.slice(6, 10), 10);
    return year >= 1990 && year <= 2100;
  }

  setExcelData(excelData: ExcelDataObject): void {
    this.clearData();
    if (!excelData || !excelData.data) {
      this.notificationsService.error(NOTIFICATIONS.GL_FILE_NOT_FOUND);
      return;
    }
    this.headers = excelData.headers?.sort();
    this.excelData = this.filterOutCorruptedValues(excelData.data);
    switch (true) {
      case (this.headers?.length > GL_MAX_COL_AMOUNT): {
        this.clearData();
        this.notificationsService.error(NOTIFICATIONS.COLUMN_SIZE_EXCEEDED_ERROR);
        break;
      }
      case (this.excelData?.length > GL_MAX_ROW_AMOUNT): {
        this.clearData();
        this.notificationsService.error(NOTIFICATIONS.ROW_SIZE_EXCEEDED_ERROR);
        break;
      }
      default: {
        this.dataForDisplay.next(this.excelData.slice(0, DATA_FOR_DISPLAY_LENGTH));
        break;
      }
    }
  }

  async getFile(event: Event): Promise<void> {
    const target = event?.target as HTMLInputElement;
    if (!target?.files[0]) {
      return;
    }
    this.file = target.files[0];
    if (this.file?.size / 1000000 > GL_MAX_FILE_SIZE_MB) {
      this.notificationsService.error(NOTIFICATIONS.FILE_SIZE_TOO_LARGE);
      this.file = null;
      return;
    }
    let res: ExcelDataObject;
    if (typeof Worker !== 'undefined') {
      const worker = new Worker(new URL('../gl-upload/gl-upload.worker', import.meta.url));

      worker.onmessage = ({ data }): void => {
        res = data;
        this.setExcelData(res);
      };
      worker.postMessage(this.file);
    } else {
      // Handle case where Web Workers are not supported
      res = await this.loadFile(this.file);
      this.setExcelData(res);
    }
  }

  setExpectedHeaders(): void {
    if (!this.headers || !this.columnSelectors.value) {
      return;
    }
    const selectedHeaders: string[] = [];
    this.setUsdCreditDebitHeaders(selectedHeaders);
    for (const excelHeader of this.headers) {
      if (selectedHeaders.includes(excelHeader)) {
        continue; // if usd headers were found and set
      }
      for (const selector of this.columnSelectors.value) {
        if (selector.title === 'Contra account #' && excelHeader.toLowerCase().includes('name')) {
          continue; // skip if it's a contra account header that contains "name" as we need only account number
        }
        for (const possibleHeaderName of selector.potentialNames) {
          if (!selector.header && this.isExcelHeaderExpected(excelHeader, possibleHeaderName)) {
            selector.header = excelHeader;
            selectedHeaders.push(excelHeader);

            if (selector.title === 'Date') {
              this.onDateColumnSelected(selector);
            }
            break;
          }
        }
      }
    }

    for (const selectedHeader of selectedHeaders) {
      this.removeSelectedHeaderFromDropdown(this.headers, selectedHeader);
    }
    this.checkMandatoryColumnsSelected();
  }

  // triggers every time a column is selected to check if they are all selected, if all selected then compares data
  checkMandatoryColumnsSelected(): boolean {
    if (!this.dataForDisplay?.value?.length) {
      return;
    }
    this.allMandatoryColumnsSelected = this.columnSelectors.value
      .filter((selector) => selector.isRequired)
      .every((selector) => selector.header && selector.header !== selectorEmptyOption);
    return this.allMandatoryColumnsSelected;
  }

  removeSelectedHeaderFromDropdown(headers: string[], selectedHeader: string): void {
    const index = headers?.indexOf(selectedHeader);
    if (index > -1) {
      headers.splice(index, 1);
    }
  }

  // converts column selected as date from excel date to regular date
  onDateColumnSelected(selector: ColumnSelector): void {
    const dateColumn = selector.header;
    this.checkReservedDataForDisplay();
    if (!this.isColumnDateConverted(this.dataForDisplay.value, dateColumn)) {
      this.dataForDisplay.value.forEach((rowData) => { // for each row of data
        // convert value at marked index using convert function
        rowData[dateColumn] = this.convertExcelDate(rowData[dateColumn]);
      });
    }
    this.checkMandatoryColumnsSelected();
  }

  convertAllDates(): Record<string, string | number>[] {
    this.excelData.forEach((data) => {
      data[this.columnSelectors.value.find((column) =>
        column.title === 'Date').header] = this.convertExcelDate(data[this.columnSelectors.value.find((column) =>
        column.title === 'Date').header]);
    });
    return this.excelData;
  }

  // separates data that is in range and out of range of selected period
  filterByDate(period: Date): ExcelGeneralLedgerDTO[] {
    const periodInDateFormat = new Date(period);
    this.reportingPeriod = periodInDateFormat.toLocaleString('en-GB').slice(0, 10);

    this.postPeriodData = this.glData.filter((data) =>
      data.transactionDate > periodInDateFormat);

    this.glData = this.glData.filter((data) =>
      data.transactionDate <= periodInDateFormat);
    return this.postPeriodData;
  }

  getHeaderRow(sheet: XLSX.WorkSheet): string[] {
    const headers: string[] = [];
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const R = range.s.r; /* start in the first row */
    /* walk every column in the range */
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })]; /* find the cell in the first row */
      if (cell && cell.t) {
        const hdr = XLSX.utils.format_cell(cell);
        headers.push(hdr);
      }
    }
    return headers;
  }

  async loadFile(excelFile: File): Promise<ExcelDataObject> {
    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(excelFile);
    const etj = await this.readerLoad(reader);
    const excelToJson: ExcelDataObject = {
      filename: excelFile.name,
      headers: etj.headers,
      data: etj.data,
    };
    return excelToJson;
  }

  readerLoad(reader: FileReader): Promise<ExcelDataObject> {
    return new Promise((resolve, reject) => {
      const excelToJson: ExcelDataObject = {
        filename: '',
        headers: [],
        data: [],
      };
      reader.onload = (e: ProgressEvent<FileReader>): void => {
        try {
          const binaryStr: string | ArrayBuffer = e.target.result;
          const wb: XLSX.WorkBook = XLSX.read(binaryStr, { type: 'binary' });
          for (const sheetName of wb.SheetNames) {
            const ws: XLSX.WorkSheet = wb.Sheets[sheetName];
            const data: Record<string, string | number>[] = XLSX.utils.sheet_to_json(ws);
            const headers = this.getHeaderRow(ws);
            excelToJson.data = data;
            excelToJson.headers = headers;
          }

          resolve(excelToJson);
        } catch (error) {
          reject(error);
        }
      };
    });
  }

  // for some reason is needed for misha's worker
  private onWorkerMessage(): void {
  }

  private clearData(): void {
    this.reservedDataForDisplay = undefined;
    this.glData = []; // empties data array before importing new data
    this.file = null;
    this.selectedHeaders = [];
    this.headers = null;
    this.dataForDisplay.next([]);
  }

  private isExcelHeaderExpected(excelHeader: string, expectedName: string): boolean {
    return excelHeader.toLowerCase() === expectedName.toLowerCase() || excelHeader.toLowerCase().includes(expectedName.toLowerCase());
  }

  /**
   * Filters out transactions from the provided transaction array that do not meet the criteria of having at least
   * a minimum number of non-null and non-undefined properties.
   *
   * As the input is a parsed excel gl transactions, we cannot directly check for the presence of a property
   * (e.g. account name, account number, date, credit/debit), so we check that at least MIN_PRESENT_PROPERTIES are present.
   */
  private filterOutCorruptedValues(data: Record<string, string | number>[]): Record<string, string | number>[] {
    return data.filter((item) => this.countNonNullProperties(item) >= MIN_PRESENT_PROPERTIES);
  }

  /**
   * Counts the number of non-null and non-undefined properties in the provided excel transaction.
   *
   * @param excelGlTransaction - The data excelGlTransaction whose properties are to be counted.
   * @returns The count of non-null and non-undefined properties.
   */
  private countNonNullProperties(excelGlTransaction: Record<string, string | number>): number {
    return Object.keys(excelGlTransaction).reduce((count, key) => (
      (excelGlTransaction[key] !== null && excelGlTransaction[key] !== undefined) ? count + 1 : count),
    0);
  }

  /**
   * Resets the data that is displayed in the component. A deep copy is used to ensure independence when modifying an object.
   */
  private checkReservedDataForDisplay(): void {
    if (!this.reservedDataForDisplay) {
      this.reservedDataForDisplay = cloneDeep(this.dataForDisplay.value);
    } else {
      this.dataForDisplay.next(cloneDeep(this.reservedDataForDisplay));
    }
  }

  private setUsdCreditDebitHeaders(selectedHeaders: string[]): void {
    const normalizedHeaders = this.headers.map((header) => header.toLowerCase().replace(/\s+/g, ''));

    const usdHeaders = { debit: null, credit: null };

    // search for debit and credit in usd
    usdHeaders.debit = this.headers[normalizedHeaders.indexOf('debit(usd)')] ||
      this.headers[normalizedHeaders.indexOf('חובה(usd)')] || null;
    usdHeaders.credit = this.headers[normalizedHeaders.indexOf('credit(usd)')] ||
      this.headers[normalizedHeaders.indexOf('זכות(usd)')] || null;

    if (usdHeaders.debit) {
      const debitSelector = this.columnSelectors.value.find((selector) => selector.title === 'Debit');
      debitSelector.header = usdHeaders.debit;
      selectedHeaders.push(usdHeaders.debit);
    }

    if (usdHeaders.credit) {
      const creditSelector = this.columnSelectors.value.find((selector) => selector.title === 'Credit');
      creditSelector.header = usdHeaders.credit;
      selectedHeaders.push(usdHeaders.credit);
    }
  }
}

/**
 * Each gl transaction has to have at least 4 properties: account name, account number, date and credit or debit
 */
const MIN_PRESENT_PROPERTIES = 4;
export const GL_MAX_COL_AMOUNT = 25;
export const GL_MAX_ROW_AMOUNT = 30000;
export const GL_MAX_FILE_SIZE_MB = 3;
export const DATA_FOR_DISPLAY_LENGTH = 5;

export const selectorEmptyOption = '-';

export const expectedHeadersMap: Map<string, string[]> = new Map([
  ['accountNumColumn', ['Account No.', 'Account #', 'Account Number']],
  ['accountNameColumn', ['Account Description', 'Account Name']],
  ['dateColumn', ['Transaction Date', 'Date', 'ת. מאזן']],
  ['journalEntryColumn', ['Entry No', 'Entry', 'מס.תנועה']],
  ['descriptionColumn', ['Details', 'פרטים']],
  ['debitColumn', ['Debit (USD)', 'חובה (USD)', 'Debit']],
  ['creditColumn', ['Credit (USD)', 'זכות (USD)', 'Credit']],
  ['contraAccountColumn', ['Contra', 'חשבון נגדי']],
]);

export const baseColumnSelectors = [
  {
    title: 'Account #', isRequired: true, header: null,
    potentialNames: expectedHeadersMap.get('accountNumColumn'),
  },
  {
    title: 'Account name', isRequired: true, header: null,
    potentialNames: expectedHeadersMap.get('accountNameColumn'),
  },
  {
    title: 'Date', isRequired: true, header: null,
    potentialNames: expectedHeadersMap.get('dateColumn'),
  },
  {
    title: 'Journal entry #', isRequired: false, header: null,
    potentialNames: expectedHeadersMap.get('journalEntryColumn'),
  },
  {
    title: 'Entry description', isRequired: false, header: null,
    potentialNames: expectedHeadersMap.get('descriptionColumn'),
  },
  {
    title: 'Debit', isRequired: true, header: null,
    potentialNames: expectedHeadersMap.get('debitColumn'),
  },
  {
    title: 'Credit', isRequired: true, header: null,
    potentialNames: expectedHeadersMap.get('creditColumn'),
  },
  {
    title: 'Contra account #', isRequired: false, header: null,
    potentialNames: expectedHeadersMap.get('contraAccountColumn'),
  },
];

export interface ExcelDataObject {
  filename: string;
  headers: string[];
  data: Record<string, string | number>[];
}
